import React, { useEffect, useState, useRef } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemSecondaryAction,
  ListItemText,
  Menu,
  MenuItem,
  Paper,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import WarningIcon from "@mui/icons-material/Warning";
import PendingIcon from "@mui/icons-material/Pending";
import ErrorIcon from "@mui/icons-material/Error";
import InfoIcon from "@mui/icons-material/Info";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import {
  SelectElement,
  TextareaAutosizeElement,
  TextFieldElement,
  useForm,
} from "react-hook-form-mui";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { fetchLLMScheduledPrompt } from "../dataaccess/fetchLLMScheduledPrompts";
import { useUser } from "../contexts/useUser";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { LLMScheduledPromptStatus } from "../API";
import { Controller } from "react-hook-form";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import AddIcon from "@mui/icons-material/Add";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import { useLLM } from "llmasaservice-client";
import { createLLMScheduledPromptAfterEdit } from "../dataaccess/createLLMScheduledPromptAfterEdit";
import { updateLLMScheduledPromptAfterEdit } from "../dataaccess/updateLLMScheduledPromptAfterEdit";
import { fetchLLMAgents } from "../dataaccess/fetchLLMAgents";

// Define schema for form validation
const schema = z.object({
  title: z.string().optional().nullable(),
  prompt: z.string().min(1, "Prompt text is required"),
  data: z.string().optional().nullable(),
  status: z.string(),
  scheduledAt: z.date().nullable(),
  repeatIntervalHours: z.number().optional().nullable(),
  agentId: z.string().min(1, "Agent selection is required"),
  followOnAgentId: z.string().optional().nullable(),
  followOnFollowOnAgentId: z.string().optional().nullable(),
  followOnTitle: z.string().optional().nullable(),
  followOnPrompt: z.string().optional().nullable(),
  followOnScheduledAtHours: z.number().optional().nullable(),
  followOnNotificationEmailAddresses: z.string().optional().nullable(),
  followOnNotificationEmailSubject: z.string().optional().nullable(),
  followOnRepeatIntervalHours: z.number().optional().nullable(),
  notificationEmailAddresses: z.string().optional().nullable(),
  notificationEmailSubject: z.string().optional().nullable(),
});

type ScheduledPromptFormType = z.infer<typeof schema>;

// Define the type for data entries
type DataEntry = {
  key: string;
  data: string;
};

const ScheduledPromptForm: React.FC = () => {
  const { id } = useParams();
  const location = useLocation();
  const selectedAgent = location.state?.selectedAgent;
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { project } = useUser();
  const isEditMode = !!id;

  // Check for type=template search parameter
  const searchParams = new URLSearchParams(location.search);
  const isTemplateType = searchParams.get('type') === 'template';
  const searchParamAgentId = searchParams.get('agentId');

  let url = "https://chat.llmasaservice.io/";
  if (
    window.location.hostname === "localhost" ||
    window.location.hostname === "dev.llmasaservice.io"
  ) {
    url = "https://chat.llmasaservice.io/dev";
  }

  // Get agentId from either route params or search params for useLLM
  const currentAgentId = searchParamAgentId;

  const { send, idle } = useLLM({
    project_id: project?.id,
    url: url,
    agent: currentAgentId,
  });

  // Query to fetch existing prompt data when editing
  const {
    data: promptData,
    isLoading: isLoadingPrompt,
    isError: isErrorPrompt,
  } = useQuery({
    queryKey: ["scheduledPrompt", id],
    queryFn: () => fetchLLMScheduledPrompt(id as string),
    enabled: isEditMode,
  });

  // Query to fetch agents for the dropdown
  const { data: agentsData, isLoading: isLoadingAgents } = useQuery({
    queryKey: ["agents", project?.id],
    queryFn: () => fetchLLMAgents(project?.id as string),
    enabled: !!project?.id,
  });

  const [entries, setEntries] = useState<DataEntry[]>([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [currentEntry, setCurrentEntry] = useState<DataEntry | null>(null);
  const [entryIndex, setEntryIndex] = useState<number | null>(null);
  const [entryKey, setEntryKey] = useState("");
  const [entryData, setEntryData] = useState("");
  const [cursorPosition, setCursorPosition] = useState<number | null>(null);
  const [contextMenu, setContextMenu] = useState<{
    mouseX: number;
    mouseY: number;
  } | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const [tabValue, setTabValue] = useState(0);

  // Set up form with react-hook-form
  const { handleSubmit, control, reset, watch, setValue } =
    useForm<ScheduledPromptFormType>({
      // Use values from the query directly, similar to PromptForm
      values: promptData?.data?.getLLMScheduledPrompt
        ? {
            title:
              promptData.data.getLLMScheduledPrompt.title || "Scheduled prompt",
            prompt: promptData.data.getLLMScheduledPrompt.prompt || "",
            data: promptData.data.getLLMScheduledPrompt.data || "[]",
            status:
              promptData.data.getLLMScheduledPrompt.status ||
              LLMScheduledPromptStatus.ACTIVE,
            scheduledAt: promptData.data.getLLMScheduledPrompt.scheduledAt
              ? new Date(promptData.data.getLLMScheduledPrompt.scheduledAt)
              : null,
            repeatIntervalHours:
              promptData.data.getLLMScheduledPrompt.repeatIntervalHours || null,
            agentId: promptData.data.getLLMScheduledPrompt.agentId || "",
            followOnAgentId:
              promptData.data.getLLMScheduledPrompt.followOnAgentId || null,
            followOnFollowOnAgentId:
              promptData.data.getLLMScheduledPrompt.followOnFollowOnAgentId || null,
            followOnTitle:
              promptData.data.getLLMScheduledPrompt.followOnTitle || null,
            followOnPrompt:
              promptData.data.getLLMScheduledPrompt.followOnPrompt || null,
            followOnScheduledAtHours:
              promptData.data.getLLMScheduledPrompt.followOnScheduledAtHours || null,
            followOnNotificationEmailAddresses:
              promptData.data.getLLMScheduledPrompt.followOnNotificationEmailAddresses || null,
            followOnNotificationEmailSubject:
              promptData.data.getLLMScheduledPrompt.followOnNotificationEmailSubject || null,
            followOnRepeatIntervalHours:
              promptData.data.getLLMScheduledPrompt.followOnRepeatIntervalHours || null,
            notificationEmailAddresses:
              promptData.data.getLLMScheduledPrompt
                .notificationEmailAddresses || null,
            notificationEmailSubject:
              promptData.data.getLLMScheduledPrompt.notificationEmailSubject ||
              null,
          }
        : undefined, // Don't provide defaults when in edit mode and data is loading
      defaultValues: !isEditMode
        ? {
            // Only provide defaults for new prompts
            title: isTemplateType ? "Template" : "Scheduled prompt",
            prompt: "",
            data: "[]",
            status: isTemplateType ? "TEMPLATE" : LLMScheduledPromptStatus.ACTIVE,
            scheduledAt: new Date(),
            repeatIntervalHours: null,
            agentId: searchParamAgentId || "",
            followOnAgentId: null,
            followOnFollowOnAgentId: null,
            followOnTitle: null,
            followOnPrompt: null,
            followOnScheduledAtHours: null,
            followOnNotificationEmailAddresses: null,
            followOnNotificationEmailSubject: null,
            followOnRepeatIntervalHours: null,
            notificationEmailAddresses: null,
            notificationEmailSubject: null,
          }
        : undefined,
      resolver: zodResolver(schema),
    });

  // Helper function to safely extract string value from potential DynamoDB format
  const extractStringValue = (value: any): string => {
    if (typeof value === 'string') {
      return value;
    }
    if (typeof value === 'object' && value !== null && value.S) {
      return value.S;
    }
    if (typeof value === 'object') {
      return JSON.stringify(value);
    }
    return String(value || '');
  };

  // Parse data field on form load and sync with entries
  useEffect(() => {
    if (promptData?.data?.getLLMScheduledPrompt?.data) {
      try {
        const parsedEntries = JSON.parse(
          promptData.data.getLLMScheduledPrompt.data
        ) as DataEntry[];
        if (Array.isArray(parsedEntries)) {
          // Clean up entries to ensure all data values are strings
          const cleanedEntries = parsedEntries.map(entry => ({
            key: extractStringValue(entry.key),
            data: extractStringValue(entry.data)
          }));
          setEntries(cleanedEntries);
        }
      } catch (error) {
        console.error("Error parsing data JSON:", error);
        setEntries([]);
      }
    }
  }, [promptData]);

  // Update form data field when entries change
  useEffect(() => {
    setValue("data", JSON.stringify(entries));
  }, [entries, setValue]);

  // Create mutation
  const createMutation = useMutation({
    mutationFn: createLLMScheduledPromptAfterEdit,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["scheduledPrompts"] });

      navigate(-1);
    },
    onError: (error) => {
      console.error("Error creating scheduled prompt:", error);
    },
  });

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: updateLLMScheduledPromptAfterEdit,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["scheduledPrompts"] });
      navigate(-1);
    },
    onError: (error) => {
      console.error("Error updating scheduled prompt:", error);
    },
  });

  // Re-schedule mutation
  const rescheduleMutation = useMutation({
    mutationFn: createLLMScheduledPromptAfterEdit,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["scheduledPrompts"] });
      // Navigate to the agents page to show the new scheduled prompt
      const selectedAgentId = watch("agentId");
      navigate(selectedAgentId ? `/agents/${selectedAgentId}?tab=scheduled` : '/agents');
    },
    onError: (error) => {
      console.error("Error re-scheduling prompt:", error);
    },
  });

  // Handle re-scheduling the prompt
  const handleReschedule = () => {
    if (!promptData?.data?.getLLMScheduledPrompt) return;
    
    const currentPrompt = promptData.data.getLLMScheduledPrompt;
    
    // Create a duplicate with scheduledAt set to now
    const rescheduleData = {
      title: currentPrompt.title || "Scheduled prompt",
      prompt: currentPrompt.prompt || "",
      data: currentPrompt.data || "[]",
      status: LLMScheduledPromptStatus.ACTIVE,
      scheduledAt: new Date(), // Set to now
      repeatIntervalHours: currentPrompt.repeatIntervalHours,
      agentId: currentPrompt.agentId,
      followOnAgentId: currentPrompt.followOnAgentId,
      followOnFollowOnAgentId: currentPrompt.followOnFollowOnAgentId,
      followOnTitle: currentPrompt.followOnTitle,
      followOnPrompt: currentPrompt.followOnPrompt,
      followOnScheduledAtHours: currentPrompt.followOnScheduledAtHours,
      followOnNotificationEmailAddresses: currentPrompt.followOnNotificationEmailAddresses,
      followOnNotificationEmailSubject: currentPrompt.followOnNotificationEmailSubject,
      followOnRepeatIntervalHours: currentPrompt.followOnRepeatIntervalHours,
      notificationEmailAddresses: currentPrompt.notificationEmailAddresses,
      notificationEmailSubject: currentPrompt.notificationEmailSubject,
      projectId: project?.id,
    };
    
    rescheduleMutation.mutate(rescheduleData as any);
  };

  const onSubmit = (data: ScheduledPromptFormType) => {
    console.log("Form submitted with data:", data);

    // Ensure data field is up to date with current entries
    const updatedData = {
      ...data,
      data: JSON.stringify(entries), // Use entries directly instead of form data
      projectId: project?.id,
    };

    if (isEditMode) {
      updateMutation.mutate({
        id: id,
        ...updatedData,
      } as any);
    } else {
      console.log("Creating new scheduled prompt with data:", updatedData);
      createMutation.mutate(updatedData as any);
    }
  };

  // Handle adding a new entry
  const handleAddEntry = () => {
    setCurrentEntry(null);
    setEntryIndex(null);
    setEntryKey("");
    setEntryData("");
    setDialogOpen(true);
  };

  // Handle editing an entry
  const handleEditEntry = (index: number) => {
    const entry = entries[index];
    setCurrentEntry(entry);
    setEntryIndex(index);
    setEntryKey(entry.key);
    setEntryData(entry.data);
    setDialogOpen(true);
  };

  // Handle removing an entry
  const handleRemoveEntry = (index: number) => {
    const newEntries = [...entries];
    newEntries.splice(index, 1);
    setEntries(newEntries);
  };

  // Handle saving an entry from the dialog
  const handleSaveEntry = () => {
    if (!entryKey.trim()) {
      return; // Don't save entries with empty keys
    }

    const newEntry: DataEntry = {
      key: entryKey.trim(),
      data: entryData.trim(),
    };

    if (entryIndex !== null) {
      // Edit existing entry
      const newEntries = [...entries];
      newEntries[entryIndex] = newEntry;
      setEntries(newEntries);
    } else {
      // Add new entry
      setEntries([...entries, newEntry]);
    }

    setDialogOpen(false);
  };

  // Add a handler for the test button
  const handleTest = () => {
    // Get the current form values
    const formValues = watch();
    const result = send(
      formValues.prompt,
      [],
      entries,
      false,
      false,
      selectedAgent?.groupId ?? null,
      null,
      new AbortController(),
      (result) => {
        console.log("Test result:", result);
        alert(`Test success: ${result}`);
      },
      (error) => {
        console.error("Test Error:", error);
        alert(`Test error: ${error}`);
      }
    );
  };

  const handleContextMenu = (event: React.MouseEvent) => {
    event.preventDefault();
    if (entries.length > 0) {
      // Capture cursor position when right-clicking
      if (
        event.target &&
        typeof (event.target as HTMLTextAreaElement).selectionStart === "number"
      ) {
        setCursorPosition((event.target as HTMLTextAreaElement).selectionStart);
      }

      setContextMenu({
        mouseX: event.clientX,
        mouseY: event.clientY,
      });
    }
  };

  // Handle context menu close
  const handleContextMenuClose = () => {
    setContextMenu(null);
  };

  // Handle inserting key at cursor position
  const handleInsertKey = (key: string) => {
    const textToInsert = `{{${key}}}`;
    const promptValue = watch("prompt") || "";

    if (cursorPosition !== null && textareaRef.current) {
      const newValue =
        promptValue.substring(0, cursorPosition) +
        textToInsert +
        promptValue.substring(cursorPosition);

      setValue("prompt", newValue, { shouldValidate: true });

      // Focus back on textarea and set cursor after inserted text
      setTimeout(() => {
        if (textareaRef.current) {
          textareaRef.current.focus();
          const newPosition = cursorPosition + textToInsert.length;
          textareaRef.current.setSelectionRange(newPosition, newPosition);
        }
      }, 0);
    } else {
      // If no cursor position, append to end
      setValue("prompt", promptValue + textToInsert, { shouldValidate: true });
    }

    handleContextMenuClose();
  };

  // Track cursor position in textarea with more generic event handling
  const handleTextareaSelect = (e: any) => {
    if (
      e &&
      e.currentTarget &&
      typeof e.currentTarget.selectionStart === "number"
    ) {
      setCursorPosition(e.currentTarget.selectionStart);
    }
  };

  const handleTextareaFocus = (e: any) => {
    // Also capture position on focus
    if (
      e &&
      e.currentTarget &&
      typeof e.currentTarget.selectionStart === "number"
    ) {
      setCursorPosition(e.currentTarget.selectionStart);
    }
  };

  // Status icon selector helper function
  const getStatusIcon = (status: string) => {
    switch (status?.toLowerCase()) {
      case "success":
        return <CheckCircleIcon color="success" />;
      case "active":
        return <AccessTimeIcon color="primary" />;
      case "scheduled":
        return <PendingIcon color="info" />;
      case "draft":
        return <InfoIcon color="disabled" />;
      case "suspended":
        return <WarningIcon color="warning" />;
      case "fail1":
      case "fail2":
      case "fail3":
      case "failed":
        return <ErrorIcon color="error" />;
      default:
        return <WarningIcon color="action" />;
    }
  };

  // Show loading for edit mode until data is loaded
  if (
    isEditMode &&
    (isLoadingPrompt || !promptData?.data?.getLLMScheduledPrompt)
  ) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
        <Typography variant="body1" sx={{ ml: 2 }}>
          Loading scheduled prompt...
        </Typography>
      </Box>
    );
  }

  if (isEditMode && isErrorPrompt) {
    return (
      <Box p={4}>
        <Typography color="error">Error loading scheduled prompt</Typography>
      </Box>
    );
  }

  // Show loading for agents data
  if (isLoadingAgents) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
        <Typography variant="body1" sx={{ ml: 2 }}>
          Loading agents...
        </Typography>
      </Box>
    );
  }

  const isSubmitting = createMutation.isPending || updateMutation.isPending || rescheduleMutation.isPending;

  // Get available agents for dropdown
  const availableAgents =
    agentsData?.data?.agentsByProjectId?.items?.filter(
      (agent) => agent?.status === "active"
    ) || [];

  // Get available agents for follow-on dropdowns (exclude selected agent to prevent recursion)
  const selectedAgentId = watch("agentId");
  const followOnAvailableAgents =
    agentsData?.data?.agentsByProjectId?.items?.filter(
      (agent) => agent?.status === "active" && agent?.id !== selectedAgentId
    ) || [];

  // Check if response tab should be shown
  const showResponseTab =
    isEditMode &&
    promptData?.data?.getLLMScheduledPrompt?.status &&
    !["ACTIVE", "DRAFT"].includes(promptData.data.getLLMScheduledPrompt.status);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  // Handle status toggle
  const currentStatus = watch("status");
  const currentTitle = watch("title");
  const handleStatusToggle = (newStatus: string) => {
    setValue("status", newStatus, { shouldValidate: true });
    
    // Update title if it matches default text
    if (newStatus === "TEMPLATE" && currentTitle === "Scheduled prompt") {
      setValue("title", "Template", { shouldValidate: true });
    } else if (newStatus === "ACTIVE" && currentTitle === "Template") {
      setValue("title", "Scheduled prompt", { shouldValidate: true });
    }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box p={2}>
        <Paper elevation={3} sx={{ p: 3 }}>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
            <Typography variant="h5">
              {isEditMode 
                ? (promptData?.data?.getLLMScheduledPrompt?.status === "TEMPLATE" 
                    ? "Edit Template" 
                    : "Edit Scheduled Prompt")
                : (currentStatus === "TEMPLATE" 
                    ? "Create Template" 
                    : "Create Scheduled Prompt")
              }
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              {/* Status Toggle */}
              {!isEditMode && (
                <Box>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button
                      variant={currentStatus === "ACTIVE" ? "contained" : "outlined"}
                      size="small"
                      onClick={() => handleStatusToggle("ACTIVE")}
                      disabled={isSubmitting}
                      sx={{ textTransform: 'none' }}
                    >
                      Scheduled
                    </Button>
                    <Button
                      variant={currentStatus === "TEMPLATE" ? "contained" : "outlined"}
                      size="small"
                      onClick={() => handleStatusToggle("TEMPLATE")}
                      disabled={isSubmitting}
                      sx={{ textTransform: 'none' }}
                    >
                      Template
                    </Button>
                  </Box>
                </Box>
              )}
              
              {isEditMode && promptData?.data?.getLLMScheduledPrompt?.status && (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  {getStatusIcon(promptData.data.getLLMScheduledPrompt.status)}
                  <Typography variant="body2" color="text.secondary">
                    {promptData.data.getLLMScheduledPrompt.status.toUpperCase()}
                  </Typography>
                  {promptData.data.getLLMScheduledPrompt.status !== "ACTIVE" && (
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={handleReschedule}
                      disabled={rescheduleMutation.isPending}
                      sx={{ ml: 1 }}
                    >
                      {rescheduleMutation.isPending ? (
                        <CircularProgress size={16} />
                      ) : (
                        "Re-schedule"
                      )}
                    </Button>
                  )}
                </Box>
              )}
            </Box>
          </Box>

          {showResponseTab && (
            <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 3 }}>
              <Tabs value={tabValue} onChange={handleTabChange}>
                <Tab label="Details" />
                <Tab label="Response" />
              </Tabs>
            </Box>
          )}

          {(!showResponseTab || tabValue === 0) && (
            <form onSubmit={handleSubmit(onSubmit)}>
              <Grid container spacing={3}>
                <Grid item xs={12} md={3}>
                  <TextFieldElement
                    control={control}
                    name="title"
                    label="Title"
                    fullWidth
                    size="small"
                    disabled={isSubmitting}
                  />
                </Grid>

                <Grid item xs={12} md={3}>
                  <SelectElement
                    control={control}
                    name="agentId"
                    label="Agent"
                    fullWidth
                    size="small"
                    disabled={isSubmitting || isLoadingAgents}
                    required
                    options={[
                      { id: "", label: "Select an agent..." },
                      ...availableAgents.map((agent) => ({
                        id: agent?.id || "",
                        label: agent?.name || "Unnamed Agent",
                      })),
                    ]}
                  />
                </Grid>

                <Grid item xs={12} md={3}>
                  <Controller
                    name="scheduledAt"
                    control={control}
                    render={({ field, fieldState: { error } }) => (
                      <DateTimePicker
                        label="Don't trigger BEFORE this date and time"
                        value={field.value ? dayjs(field.value) : null}
                        onChange={(newValue) =>
                          field.onChange(newValue ? newValue.toDate() : null)
                        }
                        disabled={currentStatus === "TEMPLATE" || isSubmitting}
                        slotProps={{
                          textField: {
                            fullWidth: true,
                            size: "small",
                            required: currentStatus !== "TEMPLATE",
                            error: !!error,
                            helperText: currentStatus === "TEMPLATE" ? "Not applicable for templates" : error?.message,
                            disabled: currentStatus === "TEMPLATE" || isSubmitting,
                          },
                        }}
                      />
                    )}
                  />
                </Grid>

                <Grid item xs={12} md={3}>
                  <TextFieldElement
                    control={control}
                    name="repeatIntervalHours"
                    label="Repeat Interval (hours)"
                    fullWidth
                    size="small"
                    type="number"
                    inputProps={{ min: 1 }}
                    disabled={currentStatus === "TEMPLATE" || isSubmitting}
                    helperText={currentStatus === "TEMPLATE" ? "Not applicable for templates" : ""}
                  />
                </Grid>

                <Grid item xs={12}>
                  <Controller
                    control={control}
                    name="prompt"
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="Prompt Text"
                        multiline
                        rows={6}
                        fullWidth
                        size="small"
                        required
                        disabled={isSubmitting}
                        inputRef={textareaRef}
                        onContextMenu={(e) => handleContextMenu(e)}
                        onSelect={(e) => handleTextareaSelect(e)}
                        onClick={(e) => handleTextareaSelect(e)}
                        onKeyUp={(e) => handleTextareaSelect(e)}
                        onFocus={(e) => handleTextareaFocus(e)}
                        error={!!control.getFieldState("prompt").error}
                        helperText={
                          control.getFieldState("prompt").error?.message
                        }
                      />
                    )}
                  />
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: "block", mt: 0.5 }}
                  >
                    Right-click to insert data variables
                  </Typography>
                </Grid>

                <Grid item xs={12}>
                  <Box sx={{ mb: 2 }}>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        mb: 1,
                      }}
                    >
                      <Typography variant="subtitle1">
                        Data - Key-value pairs for the prompt (defaults, these
                        can be overwritten at runtime)
                      </Typography>
                      <Button
                        startIcon={<AddIcon />}
                        onClick={handleAddEntry}
                        disabled={isSubmitting}
                      >
                        Add Entry
                      </Button>
                    </Box>

                    <Paper
                      variant="outlined"
                      sx={{
                        p: 1,
                        maxHeight: "250px",
                        overflow: "auto",
                      }}
                    >
                      {entries.length === 0 ? (
                        <Typography
                          variant="body2"
                          sx={{
                            p: 1,
                            textAlign: "center",
                            color: "text.secondary",
                          }}
                        >
                          No entries added yet. Click "Add Entry" to add data.
                        </Typography>
                      ) : (
                        <List dense>
                          {entries.map((entry, index) => (
                            <ListItem
                              key={index}
                              divider={index < entries.length - 1}
                              sx={{ p: 0.5, minHeight: "40px" }}
                            >
                              <ListItemText
                                primary={entry.key}
                                secondary={entry.data}
                                primaryTypographyProps={{
                                  fontWeight: "bold",
                                }}
                              />
                              <ListItemSecondaryAction>
                                <IconButton
                                  edge="end"
                                  aria-label="edit"
                                  onClick={() => handleEditEntry(index)}
                                  disabled={isSubmitting}
                                >
                                  <EditIcon fontSize="small" />
                                </IconButton>
                                <IconButton
                                  edge="end"
                                  aria-label="delete"
                                  onClick={() => handleRemoveEntry(index)}
                                  disabled={isSubmitting}
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </ListItemSecondaryAction>
                            </ListItem>
                          ))}
                        </List>
                      )}
                    </Paper>

                    {/* Use proper form registration for hidden field */}
                    <input type="hidden" {...control.register("data")} />
                  </Box>
                </Grid>

                <Grid item xs={12}>
                  <Accordion>
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      aria-controls="notification-content"
                      id="notification-header"
                    >
                      <Box>
                        <Typography variant="h6">
                          Notification
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Configure email notifications for prompt completion
                        </Typography>
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Grid container spacing={3} sx={{ mt: 1 }}>
                        <Grid item xs={12} md={6}>
                          <TextFieldElement
                            control={control}
                            name="notificationEmailAddresses"
                            label="Notification Email Addresses"
                            fullWidth
                            size="small"
                            disabled={isSubmitting}
                            helperText="Comma-separated list of email addresses"
                          />
                        </Grid>

                        <Grid item xs={12} md={6}>
                          <TextFieldElement
                            control={control}
                            name="notificationEmailSubject"
                            label="Email Subject for Notification"
                            fullWidth
                            size="small"
                            disabled={isSubmitting}
                          />
                        </Grid>
                      </Grid>
                    </AccordionDetails>
                  </Accordion>
                </Grid>

                <Grid item xs={12}>
                  <Accordion>
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      aria-controls="followon-content"
                      id="followon-header"
                    >
                      <Box>
                        <Typography variant="h6">
                          Follow-on Prompt Configuration
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Send results to another agent for additional processing
                        </Typography>
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Grid container spacing={3} sx={{ mt: 1 }}>
                        <Grid item xs={12} md={4}>
                          <TextFieldElement
                            control={control}
                            name="followOnTitle"
                            label="Follow-on Title"
                            fullWidth
                            size="small"
                            disabled={isSubmitting}
                            helperText="Title for the follow-on scheduled prompt"
                          />
                        </Grid>

                        <Grid item xs={12} md={4}>
                          <TextFieldElement
                            control={control}
                            name="followOnScheduledAtHours"
                            label="Follow-on Delay (hours)"
                            fullWidth
                            size="small"
                            type="number"
                            inputProps={{ min: 0 }}
                            helperText="Hours to wait before executing follow-on prompt"
                            disabled={isSubmitting}
                          />
                        </Grid>

                        <Grid item xs={12} md={4}>
                          <SelectElement
                            control={control}
                            name="followOnAgentId"
                            label="Send results to agent"
                            fullWidth
                            size="small"
                            disabled={isSubmitting || isLoadingAgents}
                            options={[
                              { id: "", label: "None" },
                              ...followOnAvailableAgents.map((agent) => ({
                                id: agent?.id || "",
                                label: agent?.name || "Unnamed Agent",
                              })),
                            ]}
                            helperText="Agent to receive the results of this prompt execution"
                          />
                        </Grid>

                        <Grid item xs={12}>
                          <TextFieldElement
                            control={control}
                            name="followOnPrompt"
                            label="Follow-on Prompt Text"
                            multiline
                            rows={4}
                            fullWidth
                            size="small"
                            disabled={isSubmitting}
                            helperText="Prompt text for the follow-on agent, can use data variables from this prompt or {{response}} to refer to the results of this prompt"
                          />
                        </Grid>

                        <Grid item xs={12} md={6}>
                          <SelectElement
                            control={control}
                            name="followOnFollowOnAgentId"
                            label="Follow-on's Next Agent"
                            fullWidth
                            size="small"
                            disabled={isSubmitting || isLoadingAgents}
                            options={[
                              { id: "", label: "None" },
                              ...followOnAvailableAgents.map((agent) => ({
                                id: agent?.id || "",
                                label: agent?.name || "Unnamed Agent",
                              })),
                            ]}
                            helperText="Agent for the follow-on prompt to send its results to (this is the one AFTER this one)"
                          />
                        </Grid>

                        <Grid item xs={12} md={6}>
                          <TextFieldElement
                            control={control}
                            name="followOnRepeatIntervalHours"
                            label="Follow-on Repeat Interval (hours)"
                            fullWidth
                            size="small"
                            type="number"
                            inputProps={{ min: 1 }}
                            helperText="Repeat interval for the follow-on prompt"
                            disabled={isSubmitting}
                          />
                        </Grid>

                        <Grid item xs={12} md={6}>
                          <TextFieldElement
                            control={control}
                            name="followOnNotificationEmailAddresses"
                            label="Follow-on Email Addresses"
                            fullWidth
                            size="small"
                            disabled={isSubmitting}
                          />
                        </Grid>

                        <Grid item xs={12} md={6}>
                          <TextFieldElement
                            control={control}
                            name="followOnNotificationEmailSubject"
                            label="Follow-on Email Subject"
                            fullWidth
                            size="small"
                            disabled={isSubmitting}
                          />
                        </Grid>
                      </Grid>
                    </AccordionDetails>
                  </Accordion>
                </Grid>

                <Grid
                  item
                  xs={12}
                  sx={{
                    display: "flex",
                    gap: 2,
                    mt: 2,
                    justifyContent: "space-between",
                  }}
                >
                  <Box sx={{ display: "flex", gap: 2 }}>
                    <Button
                      variant="contained"
                      color="primary"
                      type="submit"
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? (
                        <CircularProgress size={24} />
                      ) : isEditMode ? (
                        promptData?.data?.getLLMScheduledPrompt?.status === "TEMPLATE" 
                          ? "Update Template" 
                          : "Update Scheduled Prompt"
                      ) : (
                        currentStatus === "TEMPLATE" 
                          ? "Create Template" 
                          : "Create Scheduled Prompt"
                      )}
                    </Button>

                    <Button
                      variant="outlined"
                      onClick={() => reset()}
                      disabled={isSubmitting}
                    >
                      Reset
                    </Button>

                    <Button
                      variant="outlined"
                      onClick={() => navigate(selectedAgentId ? `/agents/${selectedAgentId}?tab=scheduled` : '/agents')}
                      disabled={isSubmitting}
                    >
                      Cancel
                    </Button>
                  </Box>

                  <Button
                    variant="outlined"
                    color="secondary"
                    onClick={handleTest}
                    disabled={isSubmitting || !idle}
                    startIcon={
                      idle ? <PlayArrowIcon /> : <CircularProgress size={16} />
                    }
                  >
                    {idle ? "Test" : "Running"}
                  </Button>
                </Grid>
              </Grid>
            </form>
          )}

          {showResponseTab && tabValue === 1 && (
            <Box>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <TextField
                    label="Response"
                    multiline
                    rows={10}
                    fullWidth
                    size="small"
                    value={
                      promptData?.data?.getLLMScheduledPrompt?.response || ""
                    }
                    InputProps={{
                      readOnly: true,
                    }}
                  />
                </Grid>
                {promptData?.data?.getLLMScheduledPrompt?.errorMessage && (
                  <Grid item xs={12}>
                    <TextField
                      label="Error Message"
                      multiline
                      rows={4}
                      fullWidth
                      size="small"
                      value={promptData.data.getLLMScheduledPrompt.errorMessage}
                      InputProps={{
                        readOnly: true,
                      }}
                    />
                  </Grid>
                )}
                {promptData?.data?.getLLMScheduledPrompt?.processedAt && (
                  <Grid item xs={12}>
                    <TextField
                      label="Processed At"
                      fullWidth
                      size="small"
                      value={new Date(
                        promptData.data.getLLMScheduledPrompt.processedAt
                      ).toLocaleString()}
                      InputProps={{
                        readOnly: true,
                      }}
                    />
                  </Grid>
                )}
              </Grid>
            </Box>
          )}
        </Paper>
      </Box>

      {/* Dialog for adding/editing entries */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)}>
        <DialogTitle>{currentEntry ? "Edit Entry" : "Add Entry"}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Key"
            fullWidth
            size="small"
            value={entryKey}
            onChange={(e) => setEntryKey(e.target.value)}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label="Value"
            fullWidth
            size="small"
            multiline
            rows={3}
            value={entryData}
            onChange={(e) => setEntryData(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleSaveEntry} variant="contained">
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Context Menu for inserting data keys */}
      <Menu
        open={contextMenu !== null}
        onClose={handleContextMenuClose}
        anchorReference="anchorPosition"
        anchorPosition={
          contextMenu !== null
            ? { top: contextMenu.mouseY, left: contextMenu.mouseX }
            : undefined
        }
      >
        <MenuItem disabled sx={{ opacity: 0.7 }}>
          <Typography variant="body2" fontWeight="bold">
            Insert data variable:
          </Typography>
        </MenuItem>
        {entries.length === 0 ? (
          <MenuItem disabled>
            <Typography variant="body2">No data variables available</Typography>
          </MenuItem>
        ) : (
          entries.map((entry, index) => (
            <MenuItem key={index} onClick={() => handleInsertKey(entry.key)}>
              {entry.key}
            </MenuItem>
          ))
        )}
      </Menu>
    </LocalizationProvider>
  );
};

export default ScheduledPromptForm;
